import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  IconButton,
  Tooltip,
  useToast,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input
} from "@chakra-ui/react";
import { MathEditor } from "./MathLive";

// Helper to escape regex special characters
const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Caret helper functions
function getCaretCharacterOffsetWithin(element) {
  let caretOffset = 0;
  const doc = element.ownerDocument || element.document;
  const win = doc.defaultView || doc.parentWindow;
  let sel;
  if (typeof win.getSelection !== "undefined") {
    sel = win.getSelection();
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      caretOffset = preCaretRange.toString().length;
    }
  }
  return caretOffset;
}

function setCaretPosition(element, offset) {
  if (!element) return;
  const range = document.createRange();
  const sel = window.getSelection();
  
  let currentOffset = 0;
  let nodeToSelect = null;
  let nodeOffset = 0;

  function traverseNodes(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = node.nodeValue.length;
      if (currentOffset + len >= offset) {
        nodeToSelect = node;
        nodeOffset = offset - currentOffset;
        return true;
      }
      currentOffset += len;
    } else {
      for (let i = 0; i < node.childNodes.length; i++) {
        if (traverseNodes(node.childNodes[i])) {
          return true;
        }
      }
    }
    return false;
  }

  traverseNodes(element);

  if (nodeToSelect) {
    range.setStart(nodeToSelect, nodeOffset);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    
    try {
      const parent = nodeToSelect.parentElement;
      if (parent) {
        parent.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    } catch (e) {
      console.warn("Caret scroll failed:", e);
    }
  }
}

// Deterministic character alignment mapping for precise caret translation
export const buildCaretMaps = (latex) => {
  const latexToTextMap = new Array(latex.length);
  const textToLatexMap = [];
  
  let i = 0;
  let textIndex = 0;
  
  const beginDocIndex = latex.indexOf("\\begin{document}");
  if (beginDocIndex !== -1) {
    // Header section maps to 0 in visual editor
    for (let k = 0; k < beginDocIndex + 16; k++) {
      latexToTextMap[k] = 0;
    }
    i = beginDocIndex + 16;
  }
  
  while (i < latex.length) {
    const char = latex[i];
    
    if (char === "\\") {
      // 1. Environments: begin/end
      let beginEndMatch = latex.substring(i).match(/^\\(begin|end)\{([^}]+)\}/);
      if (beginEndMatch) {
        const fullLen = beginEndMatch[0].length;
        const env = beginEndMatch[2];
        // Equations are kept as editable DOM text blocks
        if (env === "equation" || env === "align" || env === "gather") {
          for (let k = 0; k < fullLen; k++) {
            latexToTextMap[i + k] = textIndex;
            textToLatexMap.push(i + k);
            textIndex++;
          }
        } else {
          // Skip figure/list container control lines
          for (let k = 0; k < fullLen; k++) {
            latexToTextMap[i + k] = textIndex;
          }
        }
        i += fullLen;
        continue;
      }
      
      // 2. Command tags: \section{, \textbf{, etc.
      let cmdMatch = latex.substring(i).match(/^\\([a-zA-Z]+)(?:\[([^\]]*)\])?\{/);
      if (cmdMatch) {
        const fullLen = cmdMatch[0].length;
        for (let k = 0; k < fullLen; k++) {
          latexToTextMap[i + k] = textIndex;
        }
        i += fullLen;
        continue;
      }
      
      // 3. \item keyword
      let itemMatch = latex.substring(i).match(/^\\item/);
      if (itemMatch) {
        const fullLen = itemMatch[0].length;
        for (let k = 0; k < fullLen; k++) {
          latexToTextMap[i + k] = textIndex;
        }
        textToLatexMap.push(i);
        textIndex += 2; // maps to list bullet space offset
        i += fullLen;
        continue;
      }
      
      // 4. \bibitem{key} entries
      let bibMatch = latex.substring(i).match(/^\\bibitem\{([^}]+)\}/);
      if (bibMatch) {
        const fullLen = bibMatch[0].length;
        const key = bibMatch[1];
        const visualKey = `[${key}] `;
        for (let k = 0; k < fullLen; k++) {
          latexToTextMap[i + k] = textIndex;
        }
        for (let k = 0; k < visualKey.length; k++) {
          textToLatexMap.push(i);
        }
        textIndex += visualKey.length;
        i += fullLen;
        continue;
      }
    }
    
    // Command closure braces and math tags
    if (char === "}" || char === "$") {
      latexToTextMap[i] = textIndex;
      i++;
      continue;
    }
    
    // Normal character mapping
    latexToTextMap[i] = textIndex;
    textToLatexMap.push(i);
    textIndex++;
    i++;
  }
  
  // Safety checks
  for (let k = 0; k < latex.length; k++) {
    if (latexToTextMap[k] === undefined) {
      latexToTextMap[k] = textIndex;
    }
  }
  
  return { latexToTextMap, textToLatexMap };
};

export const mapLatexToTextOffset = (latex, latexIndex) => {
  const { latexToTextMap } = buildCaretMaps(latex);
  if (latexIndex >= latexToTextMap.length) return latexToTextMap[latexToTextMap.length - 1] || 0;
  return latexToTextMap[latexIndex] || 0;
};

export const mapTextToLatexOffset = (latex, textOffset) => {
  const { textToLatexMap } = buildCaretMaps(latex);
  if (textOffset >= textToLatexMap.length) return textToLatexMap[textToLatexMap.length - 1] || latex.length;
  return textToLatexMap[textOffset] || 0;
};

export default function RichTextEditor({ value, onChange, theme, initialCaretOffset, onCaretChange }) {
  const [preamble, setPreamble] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [postamble, setPostamble] = useState("");
  const [activeMath, setActiveMath] = useState(null); // { id, latex }
  const toast = useToast();
  const editorRef = useRef(null);
  const lastSavedValueRef = useRef(null);
  const [findTerm, setFindTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Split and parse the LaTeX on load
  useEffect(() => {
    if (!value) return;
    
    // Skip parsing if the update matches our own internally generated value
    if (lastSavedValueRef.current !== null && value === lastSavedValueRef.current) {
      return;
    }
    lastSavedValueRef.current = value;

    let pre = "";
    let body = value;
    let post = "";

    const beginDocIndex = value.indexOf("\\begin{document}");
    const endDocIndex = value.indexOf("\\end{document}");

    if (beginDocIndex !== -1) {
      pre = value.substring(0, beginDocIndex + "\\begin{document}".length);
      if (endDocIndex !== -1) {
        body = value.substring(beginDocIndex + "\\begin{document}".length, endDocIndex);
        post = value.substring(endDocIndex);
      } else {
        body = value.substring(beginDocIndex + "\\begin{document}".length);
      }
    }

    setPreamble(pre);
    setPostamble(post);

    const parsedHtml = latexToHtml(body);
    setBodyHtml(parsedHtml);
  }, [value]);

  // Restore caret position on load if initialCaretOffset is passed
  useEffect(() => {
    if (initialCaretOffset !== undefined && initialCaretOffset !== null && editorRef.current) {
      editorRef.current.focus();
      setTimeout(() => {
        setCaretPosition(editorRef.current, initialCaretOffset);
      }, 50);
    }
  }, [bodyHtml, initialCaretOffset]);

  // Trigger search on Ctrl+F / Cmd+F shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setShowSearch(prev => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Convert LaTeX to HTML for rich-text presentation
  const latexToHtml = (latex) => {
    let html = latex;
    const mathTokens = [];

    // 0. Extract environment Block equations: \begin{equation} ... \end{equation} (and align, gather)
    html = html.replace(/\\begin\{(equation|align|gather)\}([\s\S]*?)\\end\{\1\}/g, (match, env, formula) => {
      const id = "math-" + Math.random().toString(36).substr(2, 9);
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      mathTokens.push(`<div class="rich-block-math" data-type="${env}" data-id="${id}" data-math="${encodeURIComponent(formula.trim())}" contenteditable="false">\\begin{${env}}\n${formula.trim()}\n\\end{${env}}</div>`);
      return token;
    });

    // 1. Extract block equations: $$ ... $$ or \[ ... \]
    html = html.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
      const id = "math-" + Math.random().toString(36).substr(2, 9);
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      mathTokens.push(`<div class="rich-block-math" data-id="${id}" data-math="${encodeURIComponent(formula.trim())}" contenteditable="false">$$\n${formula.trim()}\n$$</div>`);
      return token;
    });
    html = html.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
      const id = "math-" + Math.random().toString(36).substr(2, 9);
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      mathTokens.push(`<div class="rich-block-math" data-id="${id}" data-math="${encodeURIComponent(formula.trim())}" contenteditable="false">$$\n${formula.trim()}\n$$</div>`);
      return token;
    });

    // 2. Extract inline equations: $ ... $ or \( ... \)
    html = html.replace(/\$([^$]*?)\$/g, (match, formula) => {
      const id = "math-" + Math.random().toString(36).substr(2, 9);
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      mathTokens.push(`<span class="rich-inline-math" data-id="${id}" data-math="${encodeURIComponent(formula.trim())}" contenteditable="false">$${formula.trim()}$</span>`);
      return token;
    });
    html = html.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
      const id = "math-" + Math.random().toString(36).substr(2, 9);
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      mathTokens.push(`<span class="rich-inline-math" data-id="${id}" data-math="${encodeURIComponent(formula.trim())}" contenteditable="false">$${formula.trim()}$</span>`);
      return token;
    });

    // 2.5 Extract figure environments
    html = html.replace(/\\begin\{figure\*?\}([\s\S]*?)\\end\{figure\*?\}/g, (match, body) => {
      const id = "figure-" + Math.random().toString(36).substr(2, 9);
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      let caption = "";
      const capMatch = body.match(/\\caption\{([^}]*)\}/);
      if (capMatch) caption = capMatch[1];
      
      mathTokens.push(`<div class="rich-block-figure" data-id="${id}" data-raw="${encodeURIComponent(match.trim())}" contenteditable="false"><div class="rich-figure-preview">🖼️ Figure Block</div>${caption ? `<div class="rich-figure-caption">Caption: ${caption}</div>` : ""}</div>`);
      return token;
    });

    // 2.6 Extract bibitem items
    html = html.replace(/\\bibitem\{([^}]*)\}([\s\S]*?)(?=\\bibitem|\\end\{thebibliography\}|$)/g, (match, key, content) => {
      const token = `%%%MATH_TOKEN_${mathTokens.length}%%%`;
      mathTokens.push(`<div class="rich-bibitem" data-key="${key}" data-content="${encodeURIComponent(content.trim())}" contenteditable="false"><span class="rich-bibitem-key">[${key}]</span> <span class="rich-bibitem-content" contenteditable="true">${content.trim()}</span></div>`);
      return token;
    });

    // 3. Document structure (sections)
    html = html.replace(/\\section\*?\{([^}]*)\}/g, "<h2>$1</h2>");
    html = html.replace(/\\subsection\*?\{([^}]*)\}/g, "<h3>$1</h3>");
    html = html.replace(/\\subsubsection\*?\{([^}]*)\}/g, "<h4>$1</h4>");

    // 4. Basic formatting commands
    html = html.replace(/\\textbf\{([^}]*)\}/g, "<strong>$1</strong>");
    html = html.replace(/\\textit\{([^}]*)\}/g, "<em>$1</em>");
    html = html.replace(/\\emph\{([^}]*)\}/g, "<em>$1</em>");

    // 5. Custom tags / commands (e.g. \mycommand[opts]{content} or \mycommand{content})
    const reservedWords = ["section", "subsection", "subsubsection", "textbf", "textit", "emph", "begin", "end", "item"];
    html = html.replace(/\\([a-zA-Z]+)(?:\[([^\]]*)\])?\{((?:[^{}]|\{[^{}]*\})*)\}/g, (match, cmd, opts, content) => {
      if (reservedWords.includes(cmd)) return match;
      const optionAttr = opts ? `data-opts="${encodeURIComponent(opts)}"` : "";
      return `<span class="rich-custom-cmd" data-cmd="${cmd}" ${optionAttr} contenteditable="false"><span class="rich-cmd-badge">\\${cmd}${opts ? "[" + opts + "]" : ""}</span><span class="rich-cmd-content" contenteditable="true">${content}</span></span>`;
    });

    // 6. Handle lists (itemize / enumerate / items)
    html = html.replace(/\\begin\{itemize\}([\s\S]*?)\\end\{itemize\}/g, (match, content) => {
      const items = content.split("\\item").slice(1).map(i => `<li>${i.trim()}</li>`).join("");
      return `<ul>${items}</ul>`;
    });
    html = html.replace(/\\begin\{enumerate\}([\s\S]*?)\\end\{enumerate\}/g, (match, content) => {
      const items = content.split("\\item").slice(1).map(i => `<li>${i.trim()}</li>`).join("");
      return `<ol>${items}</ol>`;
    });

    // Restore Math Tokens
    mathTokens.forEach((tokenHtml, index) => {
      html = html.replace(`%%%MATH_TOKEN_${index}%%%`, tokenHtml);
    });

    // 7. Render line breaks and paragraph breaks
    const paragraphs = html.split(/\n\n+/);
    return paragraphs.map(p => {
      const trimmed = p.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("<h") || trimmed.startsWith("<ul") || trimmed.startsWith("<ol") || trimmed.startsWith("<div") || trimmed.includes("%%%MATH_TOKEN_")) {
        return trimmed;
      }
      return `<p>${trimmed}</p>`;
    }).join("\n");
  };

  // Convert HTML back to LaTeX source code
  const htmlToLatex = (container) => {
    let result = "";

    // Node traversal to generate precise LaTeX
    const traverse = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.nodeValue;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return "";
      }

      let content = "";
      for (let i = 0; i < node.childNodes.length; i++) {
        content += traverse(node.childNodes[i]);
      }

      const tagName = node.tagName.toLowerCase();

      if (tagName === "p") {
        return content + "\n\n";
      }
      if (tagName === "h2") {
        return `\\section{${content}}\n\n`;
      }
      if (tagName === "h3") {
        return `\\subsection{${content}}\n\n`;
      }
      if (tagName === "h4") {
        return `\\subsubsection{${content}}\n\n`;
      }
      if (tagName === "strong" || tagName === "b") {
        return `\\textbf{${content}}`;
      }
      if (tagName === "em" || tagName === "i") {
        return `\\textit{${content}}`;
      }
      if (tagName === "ul") {
        return `\\begin{itemize}\n${content}\\end{itemize}\n\n`;
      }
      if (tagName === "ol") {
        return `\\begin{enumerate}\n${content}\\end{enumerate}\n\n`;
      }
      if (tagName === "li") {
        return `  \\item ${content}\n`;
      }

      // Inline / Block Math
      if (node.classList.contains("rich-inline-math")) {
        const rawFormula = decodeURIComponent(node.getAttribute("data-math"));
        return `$${rawFormula}$`;
      }
      if (node.classList.contains("rich-block-math")) {
        const rawFormula = decodeURIComponent(node.getAttribute("data-math"));
        const env = node.getAttribute("data-type");
        if (env) {
          return `\n\n\\begin{${env}}\n${rawFormula}\n\\end{${env}}\n\n`;
        }
        return `\n\n\\[\n${rawFormula}\n\\]\n\n`;
      }

      if (node.classList.contains("rich-block-figure")) {
        return `\n\n` + decodeURIComponent(node.getAttribute("data-raw")) + `\n\n`;
      }

      if (node.classList.contains("rich-bibitem")) {
        const key = node.getAttribute("data-key");
        const innerContentEl = node.querySelector(".rich-bibitem-content");
        const innerContent = innerContentEl ? innerContentEl.innerText : content;
        return `\\bibitem{${key}} ${innerContent}\n`;
      }

      // Custom Commands
      if (node.classList.contains("rich-custom-cmd")) {
        const cmd = node.getAttribute("data-cmd");
        const optsAttr = node.getAttribute("data-opts");
        const opts = optsAttr ? decodeURIComponent(optsAttr) : "";
        const innerContentEl = node.querySelector(".rich-cmd-content");
        const innerContent = innerContentEl ? innerContentEl.innerText : content;
        return `\\${cmd}${opts ? "[" + opts + "]" : ""}{${innerContent}}`;
      }

      return content;
    };

    return traverse(container);
  };

  const handleBlur = () => {
    if (!editorRef.current) return;
    const bodyLatex = htmlToLatex(editorRef.current);
    const fullLatex = preamble + bodyLatex.trim() + "\n" + postamble;
    
    // Track last saved value to avoid resetting DOM in value useEffect
    lastSavedValueRef.current = fullLatex;
    
    const caretOffset = getCaretCharacterOffsetWithin(editorRef.current);
    onChange(fullLatex, caretOffset);
  };

  const handleCaretUpdate = () => {
    if (!editorRef.current || !onCaretChange) return;
    const caretOffset = getCaretCharacterOffsetWithin(editorRef.current);
    onCaretChange(caretOffset);
  };

  // Intercept clicks on math formulas to open editor
  const handleEditorClick = (e) => {
    const mathEl = e.target.closest(".rich-inline-math, .rich-block-math");
    if (mathEl) {
      const id = mathEl.getAttribute("data-id");
      const latex = decodeURIComponent(mathEl.getAttribute("data-math"));
      setActiveMath({ id, latex });
    }
  };

  const handleSaveMath = (newLatex) => {
    if (!activeMath || !editorRef.current) return;
    const mathEl = editorRef.current.querySelector(`[data-id="${activeMath.id}"]`);
    if (mathEl) {
      mathEl.setAttribute("data-math", encodeURIComponent(newLatex));
      if (mathEl.classList.contains("rich-inline-math")) {
        mathEl.innerText = `$${newLatex}$`;
      } else {
        const env = mathEl.getAttribute("data-type");
        if (env) {
          mathEl.innerText = `\\begin{${env}}\n${newLatex}\n\\end{${env}}`;
        } else {
          mathEl.innerText = `$$\n${newLatex}\n$$`;
        }
      }
      // Re-trigger latex compilation
      handleBlur();
    }
    setActiveMath(null);
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    handleBlur();
  };

  const handleFindNext = () => {
    if (!findTerm) return;
    window.find(findTerm, false, false, true, false, false, false);
  };

  const handleFindPrev = () => {
    if (!findTerm) return;
    window.find(findTerm, false, true, true, false, false, false);
  };

  const handleReplace = () => {
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.toString().toLowerCase() === findTerm.toLowerCase()) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(replaceTerm));
      handleBlur();
      handleFindNext();
    } else {
      handleFindNext();
    }
  };

  const insertCustomTag = () => {
    const cmd = prompt("Enter custom command name (e.g. mycommand):");
    if (!cmd) return;
    const content = prompt("Enter initial content:");
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const span = document.createElement("span");
      span.className = "rich-custom-cmd";
      span.setAttribute("data-cmd", cmd);
      span.setAttribute("contenteditable", "false");
      span.innerHTML = `<span class="rich-cmd-badge">\\${cmd}</span><span class="rich-cmd-content" contenteditable="true">${content || "content"}</span>`;
      range.insertNode(span);
      range.setStartAfter(span);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      handleBlur();
    }
  };

  const insertEquation = () => {
    const formula = prompt("Enter LaTeX math equation (e.g. x^2 + y^2 = z^2):") || "x^2";
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const div = document.createElement("div");
      div.className = "rich-block-math";
      const id = "math-" + Math.random().toString(36).substr(2, 9);
      div.setAttribute("data-id", id);
      div.setAttribute("data-math", encodeURIComponent(formula));
      div.setAttribute("contenteditable", "false");
      div.innerText = `$$\n${formula}\n$$`;
      range.insertNode(div);
      range.setStartAfter(div);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      handleBlur();
    }
  };

  return (
    <Box
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      bg={theme === "dark" || theme === "black" ? "#1e1e1e" : "#f5f5f4"}
      color={theme === "dark" || theme === "black" ? "#f0f0f0" : "#2d3748"}
      p={4}
      overflowY="auto"
      position="relative"
    >
      {/* Floating Search & Replace Bar */}
      {showSearch && (
        <Box
          position="absolute"
          top="15px"
          right="30px"
          zIndex="10"
          bg={theme === "dark" || theme === "black" ? "rgba(45, 55, 72, 0.95)" : "rgba(255, 255, 255, 0.95)"}
          backdropFilter="blur(8px)"
          border="1px solid"
          borderColor={theme === "dark" || theme === "black" ? "gray.700" : "gray.200"}
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
          p={3}
          borderRadius="xl"
          w="320px"
        >
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between">
              <Text fontSize="xs" fontWeight="bold" color="teal.500">FIND & REPLACE (Ctrl+F)</Text>
              <Button size="xs" variant="ghost" onClick={() => setShowSearch(false)}>✕</Button>
            </HStack>
            <HStack spacing={2}>
              <Input
                size="sm"
                placeholder="Find text..."
                value={findTerm}
                onChange={e => setFindTerm(e.target.value)}
                autoFocus
                borderRadius="md"
              />
              <Button size="sm" onClick={handleFindNext}>Next</Button>
              <Button size="sm" onClick={handleFindPrev}>Prev</Button>
            </HStack>
            <HStack spacing={2}>
              <Input
                size="sm"
                placeholder="Replace with..."
                value={replaceTerm}
                onChange={e => setReplaceTerm(e.target.value)}
                borderRadius="md"
              />
              <Button size="sm" colorScheme="teal" onClick={handleReplace}>Replace</Button>
            </HStack>
          </VStack>
        </Box>
      )}

      {/* Preamble preview (Read Only for clarity) */}
      <Box mb={4} opacity={0.6} p={2} bg="rgba(0,0,0,0.05)" borderRadius="md">
        <Text fontSize="xs" fontWeight="bold" mb={1} color="teal.500">
          DOCUMENT PREAMBLE (Read-Only)
        </Text>
        <Text fontSize="xs" fontFamily="monospace" whiteSpace="pre-wrap" maxH="80px" overflowY="auto">
          {preamble || "% No preamble found"}
        </Text>
      </Box>

      {/* Editor Content Area */}
      <Box
        ref={editorRef}
        contentEditable
        onBlur={handleBlur}
        onClick={(e) => { handleEditorClick(e); handleCaretUpdate(); }}
        onKeyUp={handleCaretUpdate}
        onMouseUp={handleCaretUpdate}
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
        outline="none"
        flex="1"
        minH="500px"
        fontSize="17px"
        fontFamily="'Inter', -apple-system, system-ui, sans-serif"
        lineHeight="1.85"
        letterSpacing="-0.01em"
        p={8}
        m={4}
        borderRadius="lg"
        boxShadow="sm"
        bg={theme === "dark" || theme === "black" ? "#222" : "#fdfcfb"}
        border="1px solid"
        borderColor={theme === "dark" || theme === "black" ? "gray.800" : "gray.100"}
        sx={{
          "p": { mb: 4 },
          "h2": { fontSize: "24px", fontWeight: "bold", mt: 6, mb: 4, color: "teal.500" },
          "h3": { fontSize: "20px", fontWeight: "semibold", mt: 5, mb: 3, color: "teal.600" },
          "h4": { fontSize: "18px", fontWeight: "medium", mt: 4, mb: 2, color: "teal.700" },
          "ul, ol": { pl: 6, mb: 4 },
          "li": { mb: 2 },
          "strong": { fontWeight: "bold" },
          "em": { fontStyle: "italic" },
          ".rich-inline-math": {
            bg: "teal.50",
            color: "teal.800",
            px: 1,
            mx: 1,
            borderRadius: "sm",
            border: "1px solid",
            borderColor: "teal.100",
            cursor: "pointer",
            _hover: { bg: "teal.100" }
          },
          ".rich-block-math": {
            bg: "teal.50",
            color: "teal.800",
            p: 3,
            my: 4,
            borderRadius: "md",
            border: "1px solid",
            borderColor: "teal.100",
            textAlign: "center",
            fontFamily: "monospace",
            cursor: "pointer",
            _hover: { bg: "teal.100" }
          },
          ".rich-custom-cmd": {
            display: "inline-flex",
            alignItems: "center",
            bg: "purple.50",
            border: "1px dashed",
            borderColor: "purple.200",
            borderRadius: "full",
            px: 2.5,
            py: 0.5,
            mx: 1,
            my: 0.5,
            verticalAlign: "middle"
          },
          ".rich-cmd-badge": {
            fontSize: "11px",
            fontWeight: "bold",
            color: "purple.700",
            mr: 1.5,
            userSelect: "none"
          },
          ".rich-cmd-content": {
            color: "purple.900",
            outline: "none",
            minW: "15px"
          },
          ".rich-block-figure": {
            bg: "orange.50",
            color: "orange.800",
            p: 4,
            my: 4,
            borderRadius: "md",
            border: "1px solid",
            borderColor: "orange.100",
            textAlign: "center"
          },
          ".rich-figure-preview": {
            fontSize: "18px",
            fontWeight: "bold",
            mb: 2
          },
          ".rich-figure-caption": {
            fontSize: "14px",
            fontStyle: "italic"
          },
          ".rich-bibitem": {
            bg: "blue.50",
            color: "blue.800",
            p: 3,
            my: 3,
            borderRadius: "md",
            border: "1px solid",
            borderColor: "blue.100",
            display: "flex",
            gap: 2,
            alignItems: "baseline"
          },
          ".rich-bibitem-key": {
            fontWeight: "bold",
            color: "blue.700"
          },
          ".rich-bibitem-content": {
            outline: "none",
            flex: 1
          }
        }}
      />

      {/* Math Editor Modal */}
      <Modal isOpen={!!activeMath} onClose={() => setActiveMath(null)} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg={theme === "dark" || theme === "black" ? "#2d3748" : "#fff"}>
          <ModalHeader>Edit Equation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {activeMath && (
              <VStack spacing={4} align="stretch">
                <MathEditor
                  value={activeMath.latex}
                  onChange={(latex) => setActiveMath(prev => ({ ...prev, latex }))}
                />
                <Text fontSize="xs" color="gray.500">
                  You can edit the LaTeX formula directly or use the math keyboard.
                </Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setActiveMath(null)}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={() => handleSaveMath(activeMath.latex)}>
              Save Equation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
